import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import fetchMoreUpdateQueryPreviousFix from "apollo/fetchMoreUpdateQueryPreviousFix";
import usePolledQuery from "apollo/usePolledQuery";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import { SearchNamedWebhooksIntegrationOutput } from "types/generated";
import { uniqByKey } from "utils/uniq";

import { initialSortDirection, initialSortOption, ITEMS_LIMIT } from "./constants";
import { SEARCH_WEBHOOKS } from "./gql";

type SearchWebhooksData = {
  searchNamedWebhooksIntegrations: SearchNamedWebhooksIntegrationOutput;
};

const useWebhooks = () => {
  const { onError } = useTypedContext(FlashContext);

  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(searchParams, initialSortOption, initialSortDirection),
    [searchParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(searchParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [searchParams]);

  const { error, loading, data, previousData, fetchMore } = usePolledQuery<SearchWebhooksData>(
    SEARCH_WEBHOOKS,
    {
      variables: {
        input: {
          first: ITEMS_LIMIT,
          after: null,
          fullTextSearch: searchInput,
          predicates,
          ...(sortOptionFields && { orderBy: sortOptionFields }),
        },
      },
      pollingGroup: PollingIntervalGroups.Lists,
      // avoid request executing twice while fetchMore
      nextFetchPolicy: "cache-first",
    }
  );

  const searchWebhooks =
    data?.searchNamedWebhooksIntegrations || previousData?.searchNamedWebhooksIntegrations;

  const webhooks = useMemo(
    () => searchWebhooks?.edges.map((edge) => edge.node) || [],
    [searchWebhooks?.edges]
  );

  const loadMoreItems = async () => {
    try {
      if (
        data?.searchNamedWebhooksIntegrations.pageInfo.endCursor &&
        data?.searchNamedWebhooksIntegrations.pageInfo.hasNextPage
      ) {
        await fetchMore({
          updateQuery: (prev, { fetchMoreResult }) => {
            const previous = fetchMoreUpdateQueryPreviousFix(
              !!prev?.searchNamedWebhooksIntegrations,
              prev,
              data
            );

            if (!fetchMoreResult?.searchNamedWebhooksIntegrations?.edges?.length) {
              return previous;
            }

            const result = fetchMoreResult.searchNamedWebhooksIntegrations;

            return {
              searchNamedWebhooksIntegrations: {
                ...result,
                edges: uniqByKey(
                  [...(previous?.searchNamedWebhooksIntegrations?.edges || []), ...result.edges],
                  "cursor"
                ),
              },
            };
          },
          variables: {
            input: {
              first: ITEMS_LIMIT,
              after: data.searchNamedWebhooksIntegrations.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates,
              ...(sortOptionFields && { orderBy: sortOptionFields }),
            },
          },
        });
      }
    } catch (e) {
      onError(e);
    }
  };

  const hasRows = webhooks.length > 0;
  const hasFiltersApplied = searchInput.length > 0 || predicates.length > 0;

  return {
    webhooks,
    predicates,
    error,
    loadMoreItems,
    hasNextPage: !!data?.searchNamedWebhooksIntegrations?.pageInfo?.hasNextPage,
    loadingContent: loading && !hasRows,
    hasRows,
    hasNoResults: !!data && !hasRows && hasFiltersApplied,
  };
};

export default useWebhooks;
